import { Injectable } from '@angular/core';
import { HttpBackend, HttpClient } from '@angular/common/http';

interface IpWhoIsResponse {
  ip?: string;
  success?: boolean;
  city?: string;
  region?: string;
  country?: string;
  connection?: { org?: string; isp?: string };
}

interface IpApiResponse {
  ip?: string;
  city?: string;
  region?: string;
  country_name?: string;
  org?: string;
}

/**
 * Resolves the browser's public IP the same way https://whatismyipaddress.com
 * does: by asking an external service that sees the connection's real source
 * address. The geo providers (ipwho.is, then ipapi.co) also return the IP's
 * geolocation (city/region/country), which is cached alongside the IP. Both
 * values are cached for the whole session and attached to API requests by
 * ClientIpInterceptor as the X-Client-Public-IP / X-Client-Location headers.
 */
@Injectable({ providedIn: 'root' })
export class PublicIpService {
  private http: HttpClient;
  private publicIp: string | null = null;
  private location: string | null = null;

  constructor(handler: HttpBackend) {
    // Build HttpClient from HttpBackend so this request skips the
    // interceptor chain (no Authorization header, no loop back into
    // ClientIpInterceptor).
    this.http = new HttpClient(handler);
    this.resolvePublicIp();
  }

  getPublicIp(): string | null {
    return this.publicIp;
  }

  getLocation(): string | null {
    return this.location;
  }

  private resolvePublicIp(): void {
    // Location is IP-based (the ISP's registered city), so every PC behind
    // the same office router reports the same location - same as the IP.
    // ipwho.is is the primary provider (ipapi.co is blocked/rate-limited
    // from some networks and then returns 403).
    this.http.get<IpWhoIsResponse>('https://ipwho.is/').subscribe(
      (res) => {
        if (res && res.success !== false && res.ip) {
          this.publicIp = res.ip;
          const org = res.connection && (res.connection.org || res.connection.isp);
          this.location = this.buildLocation([res.city, res.region, res.country], org);
        } else {
          this.resolveViaIpApi();
        }
      },
      () => {
        this.resolveViaIpApi();
      }
    );
  }

  private resolveViaIpApi(): void {
    this.http.get<IpApiResponse>('https://ipapi.co/json/').subscribe(
      (res) => {
        if (res && res.ip) {
          this.publicIp = res.ip;
          this.location = this.buildLocation([res.city, res.region, res.country_name], res.org);
        } else {
          this.resolveIpOnly();
        }
      },
      () => {
        // no geo provider reachable - still resolve the IP (location stays null)
        this.resolveIpOnly();
      }
    );
  }

  private resolveIpOnly(): void {
    this.http.get<{ ip: string }>('https://api.ipify.org?format=json').subscribe(
      (res) => {
        this.publicIp = res && res.ip ? res.ip : null;
      },
      () => {
        // fallback provider in case ipify is unreachable/blocked
        this.http.get('https://api64.ipify.org', { responseType: 'text' }).subscribe(
          (ip) => {
            this.publicIp = ip ? ip.trim() : null;
          },
          () => {
            this.publicIp = null;
          }
        );
      }
    );
  }

  // Compact "Org, City, Region, Country" (org omitted when unknown) -
  // labels/formatting belong to the display layer, not the stored value
  // (keeps the varchar(255) column small, searchable and
  // presentation-independent).
  private buildLocation(
    parts: (string | undefined)[],
    org?: string
  ): string | null {
    const present = [org, ...parts].filter((part) => !!part);
    return present.length ? present.join(', ') : null;
  }
}
